/**
 * Color Drop Zone Element Player
 * Background image with hotspot zones, draggable objects, color selection
 */

import * as React from 'react';
import {
    DndContext,
    DragOverlay,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    type DragStartEvent,
    type DragEndEvent,
    ImageDropCanvas,
    HotspotMarker,
    FeedbackCard,
    cn,
    shuffleArray,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    Button,
} from '@ai-content-hub/content-kit';
import type { ElementPlayerProps, GradingResult } from '@ai-content-hub/content-kit';
import type {
    ColorDropZoneElementData,
    ColorDropZoneAnswer,
    DropZoneObject,
    DropZoneHotspot,
    ColorOption,
} from './schema';
import {
    normalizeColorDropZoneData,
    getBackgroundImageSrc,
    getBackgroundImageAlt,
    colorDropZoneElementSchema,
} from './schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { ChevronDown } from 'lucide-react';

function getObjectImageSrc(obj: DropZoneObject): string {
    const c = obj.content;
    if (c && typeof c === 'object' && 'type' in c && c.type === 'image') {
        return (c as { src?: string }).src ?? '';
    }
    return '';
}

function getObjectImageAlt(obj: DropZoneObject): string {
    const c = obj.content;
    if (c && typeof c === 'object' && 'type' in c && c.type === 'image') {
        return (c as { alt?: string }).alt ?? obj.name ?? '';
    }
    return obj.name ?? '';
}

type ImageCache = Record<string, string>;

function useImageCache(objects: DropZoneObject[]): ImageCache {
    const [cache, setCache] = React.useState<ImageCache>({});
    const fetchedRef = React.useRef<Set<string>>(new Set());
    const blobUrlsRef = React.useRef<string[]>([]);

    React.useEffect(() => {
        let cancelled = false;

        for (const obj of objects) {
            const src = getObjectImageSrc(obj);
            if (!src || fetchedRef.current.has(src)) continue;
            fetchedRef.current.add(src);

            fetch(src)
                .then((r) => r.blob())
                .then((blob) => {
                    if (cancelled) return;
                    const blobUrl = URL.createObjectURL(blob);
                    blobUrlsRef.current.push(blobUrl);
                    setCache((prev) => ({ ...prev, [src]: blobUrl }));
                })
                .catch(() => {
                    if (cancelled) return;
                    setCache((prev) => ({ ...prev, [src]: src }));
                });
        }

        return () => {
            cancelled = true;
        };
    }, [objects]);

    React.useEffect(() => {
        const ref = blobUrlsRef;
        return () => {
            ref.current.forEach((u) => URL.revokeObjectURL(u));
        };
    }, []);

    return cache;
}

function ObjectImage({
    obj,
    imageCache,
    className,
    t,
}: {
    obj: DropZoneObject;
    imageCache: ImageCache;
    className?: string;
    t: (key: string, fallback?: string) => string;
}) {
    const originalSrc = getObjectImageSrc(obj);
    const alt = getObjectImageAlt(obj);
    const src = imageCache[originalSrc] || originalSrc;
    if (!src) {
        return (
            <span className="text-xs text-muted-foreground">
                {obj.name || t('cdzObjectFallbackName')}
            </span>
        );
    }
    return <img src={src} alt={alt} className={cn('shadow-none', className)} />;
}

function ColorSelectDropdown({
    obj,
    colorPalette,
    selectedId,
    onSelect,
    disabled,
    showFeedback,
    correctColorId,
    result,
    compact = false,
    position = 'top-right',
    t,
}: {
    obj: DropZoneObject;
    colorPalette: ColorOption[];
    selectedId: string | undefined;
    onSelect: (colorId: string) => void;
    disabled: boolean;
    showFeedback: boolean;
    correctColorId: string;
    result?: GradingResult;
    compact?: boolean;
    position?: 'top-right' | 'bottom-right';
    t: (key: string, fallback?: string) => string;
}) {
    const selectedColor = colorPalette.find((c) => c.id === selectedId);
    const itemResults = result?.details?.itemResults as
        | Record<string, { zoneCorrect: boolean; colorCorrect: boolean }>
        | undefined;
    const res = itemResults?.[obj.id];
    const isCorrect = showFeedback && res?.colorCorrect;
    const isIncorrect = showFeedback && selectedId && res && !res.colorCorrect;

    const trigger = compact ? (
        <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            title={selectedColor?.label || t('selectColor')}
            className={cn(
                'h-7 w-7 rounded-full border-2 p-0 shrink-0',
                isCorrect && 'border-success',
                isIncorrect && 'border-destructive',
                !isCorrect && !isIncorrect && 'border-border'
            )}
            style={{ backgroundColor: selectedColor?.value ?? 'var(--muted)' }}
        >
            <ChevronDown className="h-3 w-3 text-white drop-shadow-md rotate-[-90deg]" />
        </Button>
    ) : (
        <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn(
                'h-8 w-full justify-between gap-1 border-2',
                isCorrect && 'border-success',
                isIncorrect && 'border-destructive'
            )}
        >
            <span className="flex items-center gap-2 truncate">
                {selectedColor ? (
                    <>
                        <span
                            className="h-4 w-4 shrink-0 rounded-full border border-border"
                            style={{ backgroundColor: selectedColor.value }}
                        />
                        <span className="truncate">{selectedColor.label}</span>
                    </>
                ) : (
                    <span className="text-muted-foreground">{t('selectColor')}</span>
                )}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
    );

    return (
        <div
            className={cn(
                'absolute z-10',
                position === 'top-right' && 'top-0.5 right-0.5',
                position === 'bottom-right' && 'bottom-0.5 right-0.5'
            )}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
        >
            <DropdownMenu>
                <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[140px]">
                    {colorPalette.map((opt) => (
                        <DropdownMenuItem
                            key={opt.id}
                            onSelect={() => !disabled && onSelect(opt.id)}
                        >
                            <span
                                className="mr-2 h-4 w-4 shrink-0 rounded-full border border-border"
                                style={{ backgroundColor: opt.value }}
                            />
                            {opt.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

export interface ColorDropZonePlayerProps
    extends ElementPlayerProps<ColorDropZoneElementData, ColorDropZoneAnswer> { }

const DROPPABLE_PREFIX = 'droppable-zone-';
const DRAGGABLE_PREFIX = 'draggable-obj-';
const RETURN_PREFIX = 'return-obj-';
const TRAY_RETURN_ID = 'tray-return-zone';

function getZoneState(
    zoneId: string,
    answer: ColorDropZoneAnswer,
    result: GradingResult | undefined,
    showFeedback: boolean,
    isReview: boolean
): 'default' | 'active' | 'correct' | 'incorrect' {
    if (!showFeedback || !result) {
        const hasPlaced = Object.values(answer).some((a) => a?.zoneId === zoneId);
        return hasPlaced ? 'active' : 'default';
    }
    const itemResults = result.details?.itemResults as
        | Record<string, { zoneCorrect: boolean; colorCorrect: boolean }>
        | undefined;
    if (!itemResults) return 'default';
    const objEntry = Object.entries(answer).find(([, a]) => a?.zoneId === zoneId);
    if (!objEntry) return 'default';
    const [objId] = objEntry;
    const res = itemResults[objId];
    if (!res) return 'default';
    const bothCorrect = res.zoneCorrect && res.colorCorrect;
    return bothCorrect ? 'correct' : 'incorrect';
}

function ObjectCard({
    obj,
    colorPalette,
    answerEntry,
    onColorSelect,
    disabled,
    showFeedback,
    result,
    correctColorId,
    compact = false,
    imageCache,
    t,
}: {
    obj: DropZoneObject;
    colorPalette: ColorOption[];
    answerEntry: { zoneId: string; colorId: string } | undefined;
    onColorSelect: (colorId: string) => void;
    disabled: boolean;
    showFeedback: boolean;
    result?: GradingResult;
    correctColorId: string;
    compact?: boolean;
    imageCache: ImageCache;
    t: (key: string, fallback?: string) => string;
}) {
    const selectedColorHex = answerEntry?.colorId
        ? colorPalette.find((c) => c.id === answerEntry.colorId)?.value
        : undefined;

    const filterId = selectedColorHex ? `color-tint-${obj.id}` : undefined;
    const svgFilter = selectedColorHex && filterId ? (
        <svg width="0" height="0" className="absolute">
            <defs>
                <filter id={filterId} colorInterpolationFilters="sRGB">
                    <feFlood floodColor={selectedColorHex} result="color" />
                    <feBlend in="SourceGraphic" in2="color" mode="multiply" />
                    <feComposite in2="SourceGraphic" operator="in" />
                </filter>
            </defs>
        </svg>
    ) : null;
    const tintStyle: React.CSSProperties | undefined = filterId
        ? { filter: `url(#${filterId})` }
        : undefined;

    const imageWrapperClass = 'relative overflow-hidden flex items-center justify-center w-full h-full isolate';
    const imageWithRecolor = (
        <div className={imageWrapperClass}>
            {svgFilter}
            <div style={tintStyle} className="w-full h-full flex items-center justify-center">
                <ObjectImage obj={obj} imageCache={imageCache} className="max-w-full max-h-full w-full h-full object-contain" t={t} />
            </div>
        </div>
    );

    const imageWithRecolorNonCompact = (
        <div className="relative overflow-hidden rounded border bg-muted/30 flex items-center justify-center w-20 h-20 min-w-20 min-h-20 isolate">
            {svgFilter}
            <div style={tintStyle} className="w-full h-full flex items-center justify-center">
                <ObjectImage obj={obj} imageCache={imageCache} className="max-w-full max-h-full" t={t} />
            </div>
        </div>
    );

    const colorDropdown = (
        <ColorSelectDropdown
            obj={obj}
            colorPalette={colorPalette}
            selectedId={answerEntry?.colorId}
            onSelect={onColorSelect}
            disabled={disabled}
            showFeedback={showFeedback && !!result}
            correctColorId={correctColorId}
            result={result}
            compact
            position="bottom-right"
            t={t}
        />
    );

    if (compact) {
        return (
            <div className="relative flex flex-col items-center w-full h-full isolate">
                <div className="flex-1 w-full overflow-hidden flex items-center justify-center">
                    {imageWithRecolor}
                </div>
                {colorDropdown}
            </div>
        );
    }

    return (
        <div className="relative flex flex-col gap-2 isolate">
            {imageWithRecolorNonCompact}
            {colorDropdown}
        </div>
    );
}

// Stable default (an inline `= {}` is a new object each render; keeps memo/effect deps stable).
const EMPTY_ANSWER: ColorDropZoneAnswer = {};

export const ColorDropZonePlayer: React.FC<ColorDropZonePlayerProps> = ({
    data,
    answer = EMPTY_ANSWER,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? colorDropZoneElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    // ELMS-2370: hide answer => no per-zone/object right/wrong marks or explanations.
    const showCorrectAnswer = config?.behavior?.showCorrectAnswer !== false;

    const matchData = React.useMemo(
        () => normalizeColorDropZoneData(data ?? {}),
        [data]
    );
    const { background, zones, colorPalette, objects, shuffleObjects, mode } = matchData;
    const isColorOnlyMode = mode === 'color_only';
    const bgSrc = getBackgroundImageSrc(background);
    const bgAlt = getBackgroundImageAlt(background);
    const bgAltResolved = bgAlt || t('cdzBackgroundAlt');
    const imageCache = useImageCache(objects);

    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
    const [isDragReturn, setIsDragReturn] = React.useState(false);

    // Same pair as MatchPlayer/OrderingPlayer: mouse needs a 5px move, touch a short press-hold
    // so a quick swipe still scrolls the page.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
        // Passing custom sensors replaces dnd-kit's defaults, so keep keyboard dragging.
        useSensor(KeyboardSensor)
    );

    const displayObjects = React.useMemo(() => {
        if (shuffleObjects && (!isReview || !showCorrectAnswer)) {
            return shuffleArray([...objects]);
        }
        return objects;
    }, [objects, shuffleObjects, isReview, showCorrectAnswer]);

    const objectsInTray = React.useMemo(() => {
        if (isColorOnlyMode) return [];
        return displayObjects.filter((o) => !answer[o.id]?.zoneId);
    }, [displayObjects, answer, isColorOnlyMode]);

    // color_only: objects are pre-placed (display fallback below). We no longer auto-emit an
    // answer on mount: the pre-placement is not a learner choice, so untouched => not answered.

    const handleDragStart = React.useCallback(
        (event: DragStartEvent) => {
            const id = String(event.active.id);
            const ret = id.startsWith(RETURN_PREFIX);
            setIsDragReturn(ret);
            if (ret) {
                setActiveDragId(id.slice(RETURN_PREFIX.length));
            } else {
                setActiveDragId(
                    id.startsWith(DRAGGABLE_PREFIX) ? id.slice(DRAGGABLE_PREFIX.length) : id
                );
            }
        },
        []
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const wasReturn = isDragReturn;
            setActiveDragId(null);
            setIsDragReturn(false);

            const { active, over } = event;
            if (!over || disabled) return;

            const activeId = String(active.id);
            const overId = String(over.id);

            const displaceOccupantAndPlace = (
                next: ColorDropZoneAnswer,
                objId: string,
                zoneId: string,
                current: { zoneId: string; colorId: string } | undefined
            ) => {
                const occupantId = Object.entries(next).find(
                    ([id, a]) => id !== objId && a?.zoneId === zoneId
                )?.[0];
                if (occupantId) {
                    const occupantCurrent = next[occupantId];
                    if (occupantCurrent?.colorId) {
                        next[occupantId] = { zoneId: '', colorId: occupantCurrent.colorId };
                    } else {
                        delete next[occupantId];
                    }
                }
                next[objId] = { ...current, zoneId };
            };

            if (wasReturn && activeId.startsWith(RETURN_PREFIX)) {
                const objId = activeId.slice(RETURN_PREFIX.length);
                const current = answer[objId];
                if (overId === TRAY_RETURN_ID) {
                    const next = { ...answer };
                    if (current?.colorId) {
                        next[objId] = { zoneId: '', colorId: current.colorId };
                    } else {
                        delete next[objId];
                    }
                    onAnswer?.(next);
                } else if (overId.startsWith(DROPPABLE_PREFIX)) {
                    const zoneId = overId.slice(DROPPABLE_PREFIX.length);
                    const next = { ...answer };
                    displaceOccupantAndPlace(next, objId, zoneId, current);
                    onAnswer?.(next);
                }
                return;
            }

            if (!activeId.startsWith(DRAGGABLE_PREFIX) || !overId.startsWith(DROPPABLE_PREFIX))
                return;
            const objId = activeId.slice(DRAGGABLE_PREFIX.length);
            const zoneId = overId.slice(DROPPABLE_PREFIX.length);
            const current = answer[objId];
            const next = { ...answer };
            displaceOccupantAndPlace(next, objId, zoneId, current);
            onAnswer?.(next);
        },
        [answer, onAnswer, disabled, isDragReturn]
    );

    const handleColorSelect = React.useCallback(
        (objId: string, colorId: string) => {
            if (disabled) return;
            const current = answer[objId];
            const obj = isColorOnlyMode ? objects.find((o) => o.id === objId) : undefined;
            const zoneId = current?.zoneId ?? obj?.correctZoneId;
            onAnswer?.({ ...answer, [objId]: { ...current, ...(zoneId ? { zoneId } : {}), colorId } as ColorDropZoneAnswer[string] });
        },
        [answer, onAnswer, disabled, isColorOnlyMode, objects]
    );

    const activeObject = activeDragId ? objects.find((o) => o.id === activeDragId) : undefined;

    if (!data || objects.length === 0) {
        return (
            <div className={cn('text-muted-foreground italic p-4', className)}>
                {t('cdzPlayerNoContent')}
            </div>
        );
    }

    return (
        <div className={cn('color-drop-zone-player space-y-4 [&_img]:shadow-none', className)}>
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <ImageDropCanvas src={bgSrc} alt={bgAltResolved} className="w-full" fitViewport translate={t}>
                    {zones.map((zone) => {
                        const placedObj = isColorOnlyMode
                            ? displayObjects.find((o) => o.correctZoneId === zone.id)
                            : displayObjects.find((o) => answer[o.id]?.zoneId === zone.id);
                        const zoneState = getZoneState(
                            zone.id,
                            answer,
                            result,
                            showFeedback && showCorrectAnswer,
                            isReview
                        );

                        return (
                            <HotspotMarker
                                key={zone.id}
                                id={`${DROPPABLE_PREFIX}${zone.id}`}
                                x={zone.x}
                                y={zone.y}
                                radius={zone.radius}
                                label={zone.label}
                                state={zoneState}
                                isDragActive={!isColorOnlyMode && !!activeDragId}
                                disabled={disabled}
                            >
                                {placedObj && (
                                    <PlacedObject
                                        obj={placedObj}
                                        colorPalette={colorPalette}
                                        answerEntry={
                                            answer[placedObj.id] ?? (isColorOnlyMode
                                                ? { zoneId: placedObj.correctZoneId, colorId: '' }
                                                : undefined)
                                        }
                                        onColorSelect={(c) => handleColorSelect(placedObj.id, c)}
                                        disabled={disabled}
                                        showFeedback={showFeedback && showCorrectAnswer}
                                        result={result}
                                        imageCache={imageCache}
                                        dragDisabled={isColorOnlyMode}
                                        t={t}
                                    />
                                )}
                            </HotspotMarker>
                        );
                    })}
                </ImageDropCanvas>

                {!isColorOnlyMode && (
                <TrayReturnZone isDragReturn={isDragReturn}>
                    <div className="flex flex-wrap gap-4">
                        {objectsInTray.map((obj) => (
                            <TrayObject
                                key={obj.id}
                                obj={obj}
                                colorPalette={colorPalette}
                                answerEntry={answer[obj.id]}
                                onColorSelect={(c) => handleColorSelect(obj.id, c)}
                                disabled={disabled}
                                showFeedback={showFeedback && showCorrectAnswer}
                                result={result}
                                imageCache={imageCache}
                                t={t}
                            />
                        ))}
                    </div>
                </TrayReturnZone>
                )}

                <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
                    {activeObject && (
                        <DragOverlayContent obj={activeObject} imageCache={imageCache} t={t} />
                    )}
                </DragOverlay>
            </DndContext>

            {/* Per-object feedback explanations */}
            {showFeedback && result && showCorrectAnswer && (
                <div className="color-drop-zone-feedback space-y-2">
                    {objects.map((obj) => {
                        const itemResults = result.details?.itemResults as
                            | Record<string, { zoneCorrect: boolean; colorCorrect: boolean }>
                            | undefined;
                        const res = itemResults?.[obj.id];
                        const isCorrect = res?.zoneCorrect && res?.colorCorrect;
                        if (isCorrect || !obj.explanation?.trim()) return null;
                        return (
                            <FeedbackCard
                                key={obj.id}
                                type="explanation"
                                title={obj.name || t('cdzObjectFallbackName')}
                                translate={t}
                            >
                                {obj.explanation}
                            </FeedbackCard>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DragOverlayContent = React.memo(function DragOverlayContent({
    obj,
    imageCache,
    t,
}: {
    obj: DropZoneObject;
    imageCache: ImageCache;
    t: (key: string, fallback?: string) => string;
}) {
    const originalSrc = getObjectImageSrc(obj);
    const src = imageCache[originalSrc] || originalSrc;
    return (
        <div className="rounded-lg border-2 border-primary bg-background p-2 max-w-[120px] max-h-[120px]">
            {src ? (
                <img
                    src={src}
                    alt={obj.name || t('cdzObjectFallbackName')}
                    className="w-full h-full object-contain"
                />
            ) : (
                <span className="text-xs text-muted-foreground">
                    {obj.name || t('cdzObjectFallbackName')}
                </span>
            )}
        </div>
    );
});

function PlacedObject({
    obj,
    colorPalette,
    answerEntry,
    onColorSelect,
    disabled,
    showFeedback,
    result,
    imageCache,
    dragDisabled = false,
    t,
}: {
    obj: DropZoneObject;
    colorPalette: ColorOption[];
    answerEntry: { zoneId: string; colorId: string } | undefined;
    onColorSelect: (colorId: string) => void;
    disabled: boolean;
    showFeedback: boolean;
    result?: GradingResult;
    imageCache: ImageCache;
    dragDisabled?: boolean;
    t: (key: string, fallback?: string) => string;
}) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `${RETURN_PREFIX}${obj.id}`,
        disabled: disabled || dragDisabled,
    });

    const opacity = obj.opacity ?? 1;

    return (
        <div
            ref={setNodeRef}
            {...(dragDisabled ? {} : { ...attributes, ...listeners })}
            style={{ opacity: isDragging ? 0.5 : opacity }}
            className={cn(
                'w-full h-full flex flex-col items-center justify-center',
                // touch-none: otherwise touch-action:auto lets the browser scroll the page
                // instead of starting the dnd-kit drag on touch screens.
                !dragDisabled && 'cursor-grab active:cursor-grabbing touch-none',
            )}
        >
            <ObjectCard
                obj={obj}
                colorPalette={colorPalette}
                answerEntry={answerEntry}
                onColorSelect={onColorSelect}
                disabled={disabled}
                showFeedback={showFeedback}
                result={result}
                correctColorId={obj.correctColorId}
                imageCache={imageCache}
                compact
                t={t}
            />
        </div>
    );
}

function TrayObject({
    obj,
    colorPalette,
    answerEntry,
    onColorSelect,
    disabled,
    showFeedback,
    result,
    imageCache,
    t,
}: {
    obj: DropZoneObject;
    colorPalette: ColorOption[];
    answerEntry: { zoneId: string; colorId: string } | undefined;
    onColorSelect: (colorId: string) => void;
    disabled: boolean;
    showFeedback: boolean;
    result?: GradingResult;
    imageCache: ImageCache;
    t: (key: string, fallback?: string) => string;
}) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `${DRAGGABLE_PREFIX}${obj.id}`,
        disabled,
    });

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={cn(
                'flex flex-col gap-2 rounded-lg border bg-muted/30 p-3 cursor-grab active:cursor-grabbing touch-none transition-transform',
                isDragging && 'opacity-50 scale-95',
                // EAA-1680: playful-skin pressable-chip hooks for this
                // draggable tray token — see
                // docs/PLAN-Playful-Interactive-Options.md Phase 4d. The
                // drop targets themselves (HotspotMarker) are left
                // untouched pending visual QA of a depth edge over an
                // image (per the plan's Phase 4.6 note).
                'content-chip',
                disabled && 'content-chip--static',
                isDragging && 'content-chip--dragging'
            )}
        >
            <div className="text-sm font-medium truncate max-w-[100px]">
                {obj.name || t('cdzObjectFallbackName')}
            </div>
            <ObjectCard
                obj={obj}
                colorPalette={colorPalette}
                answerEntry={answerEntry}
                onColorSelect={onColorSelect}
                disabled={disabled}
                showFeedback={showFeedback}
                result={result}
                correctColorId={obj.correctColorId}
                imageCache={imageCache}
                compact={false}
                t={t}
            />
        </div>
    );
}

function TrayReturnZone({
    children,
    isDragReturn,
}: {
    children: React.ReactNode;
    isDragReturn: boolean;
}) {
    const { setNodeRef, isOver } = useDroppable({ id: TRAY_RETURN_ID });
    return (
        <div
            ref={setNodeRef}
            className={cn(
                'rounded-lg border-2 border-dashed p-4 min-h-[120px] transition-colors',
                isDragReturn && !isOver && 'border-primary/30 bg-primary/[0.02]',
                isDragReturn && isOver && 'border-primary bg-primary/10'
            )}
        >
            {children}
        </div>
    );
}

ColorDropZonePlayer.displayName = 'ColorDropZonePlayer';
